import React from "react";
import Layout from "@/components/Layout";
import Hero from "@/components/sections/Hero";
import TrackingSection from "@/components/tracking/TrackingSection";
import JourneySection from "@/components/journey/JourneySection";
import ProofSection from "@/components/sections/Proof";
import CtaBand from "@/components/sections/CtaBand";
import AboutSection from "@/components/sections/About";
import ServicesSection from "@/components/sections/Services";
import WhyUsSection from "@/components/sections/WhyUs";
import ShippingModes from "@/components/sections/ShippingModes";
import QuickAnswers from "@/components/sections/QuickAnswers";
import LocationsSection from "@/components/sections/Locations";
import SEO from "@/components/SEO";
import { useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { organizationSchema, websiteSchema, serviceSchema, faqSchema, branchesSchema } from "@/lib/structuredData";
import { BRANCHES } from "@/lib/branches";

export default function HomePage() {
  const { t, lang } = useLang();
  const { search } = useLocation();
  const initialTrack = new URLSearchParams(search).get("track") || "";

  const seoTitles = {
    ar: "مسك كارجو - شحن دولي من تركيا والصين إلى سوريا | Misk Cargo",
    en: "Misk Cargo | Shipping from Türkiye and China to Syria, with Live Tracking",
    tr: "Misk Cargo - Türkiye ve Çin'den Suriye'ye Uluslararası Kargo",
    "zh-CN": "Misk Cargo - 土耳其和中国到叙利亚的国际货运服务"
  };

  const seoDescriptions = {
    ar: "مسك كارجو - شريكك الموثوق للشحن الدولي من تركيا والصين إلى سوريا. خدمات شحن بري وبحري وجوي، تخليص جمركي، تخزين ومتابعة كاملة. مكاتب في إسطنبول ودمشق وحلب.",
    en: "Misk Cargo ships goods from Türkiye and China to Damascus and Aleppo by road, sea and air, with customs clearance, warehousing and live online tracking. Offices in Istanbul, Damascus and Aleppo.",
    tr: "Misk Cargo - Türkiye ve Çin'den Suriye'ye uluslararası kargo için güvenilir ortağınız. Kara, deniz ve havayolu taşımacılığı, gümrük işlemleri, depolama ve tam takip. İstanbul, Şam ve Halep'te ofisler.",
    "zh-CN": "Misk Cargo - 您从土耳其和中国到叙利亚国际货运的可靠合作伙伴。提供陆运、海运和空运服务，清关、仓储和全程追踪。在伊斯坦布尔、大马士革和阿勒颇设有办事处。"
  };

  const h1Titles = {
    ar: "مسك كارجو - شحن دولي موثوق من تركيا والصين إلى سوريا",
    en: "Misk Cargo - Reliable International Shipping from Turkey and China to Syria",
    tr: "Misk Cargo - Türkiye ve Çin'den Suriye'ye Güvenilir Uluslararası Kargo",
    "zh-CN": "Misk Cargo - 从土耳其和中国到叙利亚的可靠国际货运"
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      websiteSchema,
      organizationSchema,
      serviceSchema,
      ...branchesSchema(BRANCHES),
      // The Quick answers section below, mirrored for search and answer engines.
      Object.fromEntries(
        Object.entries(faqSchema(t.answers.items.map((i) => ({ question: i.q, answer: i.a })))).filter(([k]) => k !== "@context"),
      ),
    ]
  };

  return (
    <Layout bleed>
      <SEO
        title={seoTitles[lang]}
        description={seoDescriptions[lang]}
        url="/"
        structuredData={structuredData}
      />
      {/* Hidden H1 for SEO - visible title is in Hero */}
      <h1 className="sr-only">{h1Titles[lang]}</h1>
      <Hero />
      <TrackingSection initialNumber={initialTrack} />
      <AboutSection />
      <JourneySection />
      <ServicesSection />
      <ProofSection />
      <WhyUsSection />
      <ShippingModes />
      <LocationsSection />
      <QuickAnswers />
      <CtaBand />
    </Layout>
  );
}
